import shaka from "shaka-player/dist/shaka-player.compiled";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import type { PlayerContext } from "@/types/player.type";
import type { NamedTrack } from "@/lib/player/trackLanguage";
import { useAppStore } from "@/stores/useApp";
import { isIOSDevice } from "@/lib/isIOS";
import { getDeviceContext } from "@/lib/userAgent";
import { createPlaybackWatchdog } from "@/lib/player/playbackWatchdog";
import {
  preferredAudioLanguage,
  resolveTrackSelection,
  trackName,
} from "@/lib/player/trackLanguage";
import {
  addPlayerBreadcrumb,
  isSameShakaError,
  reportPlayerIssue,
} from "@/lib/player/playerReporting";

interface useShakaParams {
  url: string;
  videoElementRef: React.RefObject<HTMLVideoElement | null>;
  onEndReached?: () => void;
  onProgressChange?: (progress: number) => void;
  /**
   * Cap the variant picked at the element's own size — for a stream drawn
   * in a small box (the series page's trailer), where Shaka would otherwise
   * fetch and decode the tallest variant the bandwidth allows. Off for the
   * player, which fills the viewport.
   */
  restrictToElementSize?: boolean;
  /**
   * Apply the locale's audio and subtitle rule (`applyTrackSelection`). Off
   * for a muted stream (the series page's trailer): the subtitles that rule
   * turns on for an audio outside the locale would be drawn over the hero.
   */
  selectTracks?: boolean;
  /**
   * Keep the `progress` state (a re-render of the caller per tick). Off for a
   * consumer that routes the ticks elsewhere (`onTimeChange`, the SHO-815
   * engine's store) and must not re-render on them.
   */
  trackProgress?: boolean;
  /** Every throttled tick and `loadedmetadata`: the element's time and duration in seconds (0 until known). */
  onTimeChange?: (currentTime: number, duration: number) => void;
}

// `hlsContext` is a thin adapter over shaka.Player exposing only the track
// members the player UI reads (subtitle/audio lists + selection). The field
// name is kept from the previous hls.js engine to avoid churn across consumers.
interface useShakaReturn {
  progress: number;
  isPlaying: boolean;
  isReady: boolean;
  /**
   * Shaka's `buffering` event OR the element's own `waiting` (until `playing`
   * or `pause`): the first covers the MSE rebuffer hold, which fires no
   * `waiting`; the second the native-HLS stalls (`rebufferingGoal` 0 keeps
   * Shaka's poller off there).
   */
  isBuffering: boolean;
  hlsContext: PlayerContext | null;
}

// The active UI locale, read from i18next (the resolved answer) rather than
// from the store, like every other locale consumer.
const activeLocale = () => i18n.resolvedLanguage ?? "";

// The getters below are re-read on every render of the player, so the locale and
// the "und" fallback are resolved once per list rather than once per track.
const menuTracks = (tracks: ReadonlyArray<NamedTrack>) => {
  const locale = activeLocale();
  const undeterminedLabel = i18n.t("player_default_track_label_web");
  return tracks.map((track, index) => ({
    id: index,
    name: trackName(track, locale, undeterminedLabel),
    language: track.language,
  }));
};

/** The `preferredAudio` hint — the same rule `resolveTrackSelection` applies. */
const wantedAudioLanguage = () =>
  preferredAudioLanguage(
    useAppStore.getState().audioPreference,
    activeLocale(),
  );

/**
 * Applies the audio and text tracks `resolveTrackSelection` picks for the
 * current stream (the rule itself lives in `trackLanguage.ts`, unit-tested).
 * Selection is skipped when the track is already active — this runs on every
 * `play`, and re-selecting would flush the buffer each time.
 */
const applyTrackSelection = (player: shaka.Player) => {
  const { audioPreference, subtitlesPreference } = useAppStore.getState();
  const textTracks = player.getTextTracks();

  const { audio, text } = resolveTrackSelection({
    audioTracks: player.getAudioTracks(),
    textTracks,
    locale: activeLocale(),
    audioPreference,
    subtitlesPreference,
  });
  if (audio && !audio.active) player.selectAudioTrack(audio);

  if (text) {
    if (!text.active) player.selectTextTrack(text);
  } else if (textTracks.some((track) => track.active)) {
    player.selectTextTrack(null);
  }
};

const buildHlsAdapter = (player: shaka.Player): PlayerContext => ({
  get allSubtitleTracks() {
    // `id` is the list index — the preference modal selects by it and the
    // subtitleTrack setter resolves it against getTextTracks(). Shaka's own
    // t.id is a global counter, not an index.
    return menuTracks(player.getTextTracks());
  },
  get allAudioTracks() {
    return menuTracks(player.getAudioTracks());
  },
  get subtitleTrack() {
    return player.getTextTracks().findIndex((t) => t.active);
  },
  set subtitleTrack(idx: number) {
    if (idx === -1) {
      player.selectTextTrack(null);
      return;
    }
    player.selectTextTrack(player.getTextTracks()[idx]);
  },
  get audioTrack() {
    return player.getAudioTracks().findIndex((t) => t.active);
  },
  set audioTrack(idx: number) {
    player.selectAudioTrack(player.getAudioTracks()[idx]);
  },
});

const useShaka = ({
  url,
  videoElementRef,
  onEndReached,
  onProgressChange,
  restrictToElementSize = false,
  selectTracks = true,
  trackProgress = true,
  onTimeChange,
}: useShakaParams): useShakaReturn => {
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  // The url the element can play, not a boolean: in the commit where `url`
  // changes the cleanup's reset is still queued, so a boolean would read
  // `true` for the new url during that one render — and the SHO-815 player's
  // autoplay effect runs in it (`VideoEngine`), on the old media.
  const [readyUrl, setReadyUrl] = useState<string | null>(null);
  const isReady = readyUrl === url;
  const [shakaBuffering, setShakaBuffering] = useState(false);
  const [elementWaiting, setElementWaiting] = useState(false);
  const [hlsContext, setHlsContextState] = useState<PlayerContext | null>(null);

  const onEndReachedRef = useRef(onEndReached);
  const onProgressChangeRef = useRef(onProgressChange);
  const onTimeChangeRef = useRef(onTimeChange);
  onEndReachedRef.current = onEndReached;
  onProgressChangeRef.current = onProgressChange;
  onTimeChangeRef.current = onTimeChange;

  const playerRef = useRef<shaka.Player | null>(null);
  // The previous instance's teardown, awaited before the next `attach` on the
  // same element: `destroy()` unloads asynchronously and would otherwise clear
  // the `src` the next `load()` just set. Only a hook whose `url` changes in
  // place sees this (the SHO-815 player keeps one `<video>` across episodes);
  // a fresh element has nothing pending here.
  const teardownRef = useRef<Promise<void>>(Promise.resolve());
  const resolvedLanguage = useTranslation().i18n.resolvedLanguage;

  useEffect(() => {
    const video = videoElementRef.current;
    if (!video || !url) return;

    const nativeHls = isIOSDevice();
    const reportContext = {
      device: getDeviceContext(),
      nativeHls,
      manifestUrl: url,
    };

    if (!shaka.Player.isBrowserSupported()) {
      reportPlayerIssue({ type: "browser_unsupported", ...reportContext });
      return;
    }

    const ctrl = new AbortController();
    const isAborted = () => ctrl.signal.aborted;
    let rafId = 0;
    let lastEmit = 0;

    // Engine is chosen per-platform:
    //  • iOS (every browser is WebKit): native HLS — MSE/ManagedMediaSource
    //    isn't reliable there, the player breaks without it.
    //  • everywhere else: MSE. Chrome answers
    //    canPlayType('application/vnd.apple.mpegurl') = "maybe", so preferring
    //    native would push it to native src= playback, where it exposes NONE of
    //    the HLS subtitle renditions (getTextTracks() stays empty → the subtitle
    //    menu, gated on subtitles.length > 0, never appears). MSE surfaces them.
    // isIOSDevice (not isIOS) so desktop Mac stays on MSE and keeps subtitles.
    const player = new shaka.Player();
    playerRef.current = player;
    // Only the language is ours to pick — the rest of the shape (channelCount,
    // codec, role…) keeps Shaka's own defaults, which a literal would silently
    // override (channelCount: 0 disables the stereo restriction, it is not the
    // neutral value). Arrays are replaced, not merged, hence the spread.
    const [defaultAudioPreference] = player.getConfiguration().preferredAudio;
    player.configure({
      // Hint the audio language before load() so the wanted rendition is the
      // one fetched, instead of pulling the manifest default and switching
      // right after. Same priority as `applyTrackSelection`, which still runs
      // after load — it owns the subtitles and the exact matching.
      preferredAudio: [
        { ...defaultAudioPreference, language: wantedAudioLanguage() },
      ],
      // Our TS variants carry a muxed English audio track next to the separate
      // per-language renditions, and only the transmuxer drops it. Shaka skips
      // it whenever MSE accepts the type, which WebKit does for `video/mp2t`
      // with a video-only codec string — Safari then plays that English audio
      // on top of the selected one. Unconditional: iOS never reaches
      // MediaSourceEngine (native HLS), Firefox transmuxed already, and Chrome
      // buffers the same raw TS, so it is where the contract spec sees this.
      mediaSource: { forceTransmux: true },
      abr: { restrictToElementSize },
      streaming: {
        preferNativeHls: nativeHls,
        bufferingGoal: 15,
        // 0 on native HLS, 2 on MSE, and both halves matter: any non-zero value
        // in src= mode lets Shaka hold playbackRate at 0 just short of the end
        // (no `ended`, no auto-advance), 0 on MSE drops its start/resume
        // buffer. SHO-752, see `.claude/rules/video-player.md`.
        rebufferingGoal: nativeHls ? 0 : 2,
        bufferBehind: 15,
      },
    });

    // Detects the failures that never throw (black screen, frozen playback) by
    // watching for the absence of the expected media events. Recovered stalls
    // are breadcrumbs, not alerts.
    const watchdog = createPlaybackWatchdog({
      onFailure: (type) => reportPlayerIssue({ type, ...reportContext }),
      onStallRecovered: () =>
        addPlayerBreadcrumb("playback stall recovered", {
          browser: reportContext.device.browser,
        }),
    });

    const onCanPlay = () => {
      setReadyUrl(url);
      watchdog.onCanPlay();
    };
    const onPlay = () => {
      setIsPlaying(true);
      if (selectTracks) applyTrackSelection(player);
    };
    const onPlaying = () => {
      setElementWaiting(false);
      watchdog.onPlaying();
    };
    const onWaiting = () => {
      setElementWaiting(true);
      watchdog.onWaiting();
    };
    const onPause = () => {
      setIsPlaying(false);
      setElementWaiting(false);
      watchdog.onStopped();
    };
    const onTimeUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const d = video.duration;
        if (!d || !isFinite(d)) return;
        const now = performance.now();
        if (now - lastEmit < 100) return;
        lastEmit = now;
        const p = (video.currentTime / d) * 100;
        if (trackProgress) setProgress(p);
        onProgressChangeRef.current?.(p);
        onTimeChangeRef.current?.(video.currentTime, d);
      });
    };
    const onEnded = () => {
      watchdog.onStopped();
      onEndReachedRef.current?.();
    };
    const onLoadedMetadata = () => {
      if (selectTracks) applyTrackSelection(player);
      onTimeChangeRef.current?.(
        video.currentTime,
        Number.isFinite(video.duration) ? video.duration : 0,
      );
    };

    const listeners: Array<[string, EventListener]> = [
      ["canplay", onCanPlay],
      ["play", onPlay],
      ["playing", onPlaying],
      ["waiting", onWaiting],
      ["pause", onPause],
      ["timeupdate", onTimeUpdate],
      ["ended", onEnded],
      ["loadedmetadata", onLoadedMetadata],
    ];

    for (const [event, handler] of listeners) {
      video.addEventListener(event, handler);
    }

    // A load that fails is reported twice otherwise: Shaka dispatches the
    // `error` event with the error as `detail`, then `load()` rejects — with
    // the very same object on MSE (preload_manager.js `onError` rejects with
    // the error it dispatches), with a second one built from the same media
    // error on native HLS. Remembering the reported one lets the catch below
    // recognise it (`isSameShakaError`); a rejection that never went through
    // the event (attach, unsupported browser) is still reported as
    // `load_failed`.
    let reportedShakaError: unknown = null;
    const onShakaError = (event: any) => {
      const detail = event?.detail;
      // Only CRITICAL errors are real failures; RECOVERABLE ones are noise.
      if (detail?.severity === shaka.util.Error.Severity.CRITICAL) {
        reportedShakaError = detail;
        reportPlayerIssue({
          type: "shaka_error",
          error: detail,
          shakaError: {
            code: detail?.code,
            category: detail?.category,
            severity: detail?.severity,
          },
          ...reportContext,
        });
      } else {
        addPlayerBreadcrumb("shaka recoverable error", { code: detail?.code });
      }
    };
    player.addEventListener("error", onShakaError);
    const onShakaBuffering = (event: any) =>
      setShakaBuffering(event?.buffering === true);
    player.addEventListener("buffering", onShakaBuffering);

    (async () => {
      try {
        await teardownRef.current;
        // Through a call, not the property: narrowing the property here would
        // read as "never aborted" on the checks below, across the awaits.
        if (isAborted()) return;
        await player.attach(video);
        await player.load(url);
        if (!ctrl.signal.aborted) {
          // The goal above keys on the engine we asked for; Shaka can still
          // pick src= on its own (no MediaSource at all), before it reads
          // `preferNativeHls`. Realign on the engine it actually loaded — a
          // live `configure` stops the poller — and only when the two differ,
          // so the nominal path pays nothing.
          const usesSrcEquals =
            player.getLoadMode() === shaka.Player.LoadMode.SRC_EQUALS;
          if (usesSrcEquals !== nativeHls) {
            player.configure(
              "streaming.rebufferingGoal",
              usesSrcEquals ? 0 : 2,
            );
          }
          watchdog.onLoadResolved();
          setHlsContextState(buildHlsAdapter(player));
        }
      } catch (err) {
        if (
          !ctrl.signal.aborted &&
          !isSameShakaError(reportedShakaError, err)
        ) {
          reportPlayerIssue({
            type: "load_failed",
            error: err,
            ...reportContext,
          });
        }
      }
    })();

    return () => {
      ctrl.abort();
      watchdog.dispose();
      if (rafId) cancelAnimationFrame(rafId);
      for (const [event, handler] of listeners) {
        video.removeEventListener(event, handler);
      }
      player.removeEventListener("error", onShakaError);
      player.removeEventListener("buffering", onShakaBuffering);
      // Chained, not replaced: an instance that never attached destroys in a
      // few microtasks, and the next load would stop waiting for the one
      // still unloading the element (two quick Nexts left the third episode
      // black, its `src` cleared by the first instance's destroy).
      teardownRef.current = teardownRef.current
        .then(() => player.destroy())
        .catch(() => {});
      playerRef.current = null;
      setHlsContextState(null);
      setReadyUrl(null);
      setShakaBuffering(false);
      setElementWaiting(false);
      setIsPlaying(false);
      setProgress(0);
    };
  }, [url, videoElementRef]);

  // Re-align the tracks when the user switches the app language mid-playback —
  // the load effect only re-runs per episode, so the current one would otherwise
  // keep the previous locale's audio until the next slide. The first pass is
  // `loadedmetadata`'s job; without the ref this fires once more right after
  // every load and rebuilds both track lists for a selection already applied.
  // The ref is written only once the selection actually runs, so a switch that
  // lands while `hlsContext` is still null is applied when it arrives.
  const appliedLocaleRef = useRef(resolvedLanguage);
  useEffect(() => {
    if (!playerRef.current || !hlsContext || !selectTracks) return;
    if (appliedLocaleRef.current === resolvedLanguage) return;
    appliedLocaleRef.current = resolvedLanguage;
    applyTrackSelection(playerRef.current);
  }, [resolvedLanguage, hlsContext]);

  return {
    progress,
    isPlaying,
    isReady,
    isBuffering: shakaBuffering || elementWaiting,
    hlsContext,
  };
};

export default useShaka;
